import EditIcon from "@mui/icons-material/Edit";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import type { Dispatch, FC, ReactElement, RefObject, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { Popover as TinyPopover } from "react-tiny-popover";
import { EditTimePopover } from "../../../../components/EditTimePopover";
import { ALPHA, INK, INK_BLACK, NEUTRAL, SURFACE } from "../../../../theme/tokens";
import { smallLabelTypographySx, V1_FIELD_BORDER_RADIUS, V1_HOURS_FONT_SIZE, V1_REGULAR_FIELD_WIDTH } from "./consts";
import { LocationHoursFieldV2 } from "./LocationHoursFieldV2";
import type { LocationConfigSlimData } from "./locationConfigData";

type LocationHoursFieldCommonProps = {
	times: { start: DateTime; end: DateTime };
	open: boolean;
	/** The popover's open state setter; the box toggles it with an updater, as it always has. */
	setOpen: Dispatch<SetStateAction<boolean>>;
	onTimesChange: (times: Pick<LocationConfigSlimData, "shiftStartTime" | "shiftEndTime">) => void;
};

/**
 * v1 positions its popover beside a parent the host names. v2 opens the picker in its own MUI Menu anchored on the
 * field, so it needs no parent: a Menu is a modal layer of its own, which a dialog's focus trap (the New location
 * popup) leaves alone, where the v1 popover renders into `<body>` outside that trap and loses its focus to it.
 */
export type LocationHoursFieldProps = LocationHoursFieldCommonProps &
	(
		| {
				design: "v1";
				/** The element the time popover is positioned against. */
				popoverParentRef: RefObject<HTMLDivElement | null>;
		  }
		| {
				design: "v2";
				/** Read-only: the hours show but the picker doesn't open. */
				disabled: boolean;
		  }
	);

/** The location's shift hours: a box showing the range that opens the From/To time picker. */
export const LocationHoursField: FC<LocationHoursFieldProps> = (props): ReactElement => {
	const { times, open, setOpen, onTimesChange } = props;
	const { t } = useTranslation();
	switch (props.design) {
		case "v1":
			return (
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: 0.5,
					}}
				>
					<Typography sx={smallLabelTypographySx}>{t("schedule.template.v3.editor.setHours")}</Typography>
					<TinyPopover
						isOpen={open}
						// DEFAULT: before the parent mounts there is no element yet, and the popover positions against the page (moved verbatim).
						parentElement={props.popoverParentRef.current ?? undefined}
						onClickOutside={(e) => {
							e.stopPropagation();
							e.preventDefault();
							setOpen(false);
						}}
						positions={["right"]}
						content={
							<Box
								sx={(theme) => ({
									bgcolor: SURFACE,
									boxShadow: `0px 0px 8px 4px ${alpha(theme.palette.primary.main, ALPHA.SHADOW_HEAVY)}`,
									borderRadius: V1_FIELD_BORDER_RADIUS,
								})}
							>
								<EditTimePopover
									onTimeChange={(values) => {
										onTimesChange({
											shiftStartTime: values.newStartTime.toFormat("HH:mm"),
											shiftEndTime: values.newEndTime.toFormat("HH:mm"),
										});
										setOpen(false);
									}}
									onClose={(event) => {
										event.preventDefault();
										event.stopPropagation();
										setOpen(false);
									}}
									anchorEl={null}
									isActual={false}
									menuWrapper={false}
									disableAutoCompetePortal
									startTime={times.start}
									endTime={times.end}
								/>
							</Box>
						}
					>
						<Box
							sx={{
								border: `1px solid ${NEUTRAL[300]}`,
								borderRadius: V1_FIELD_BORDER_RADIUS,
								cursor: "pointer",
								display: "flex",
								p: 1,
								alignItems: "center",
								justifyContent: "space-between",
								width: V1_REGULAR_FIELD_WIDTH,
								fontSize: V1_HOURS_FONT_SIZE,
								fontWeight: 500,
								color: alpha(INK.SECONDARY, 0.5),
								":hover": {
									backgroundColor: alpha(INK_BLACK, 0.01),
								},
							}}
							onClick={(_) => setOpen((prevState) => !prevState)}
						>
							{times?.start.isValid && times.end.isValid
								? `${times.start.toFormat("h:mma")} - ${times.end.toFormat("h:mma")}`
								: t("schedule.template.v3.editor.setHours")}
							<EditIcon fontSize={"inherit"} />
						</Box>
					</TinyPopover>
				</Box>
			);
		case "v2":
			return <LocationHoursFieldV2 times={times} open={open} setOpen={setOpen} onTimesChange={onTimesChange} disabled={props.disabled} />;
	}
};
